import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";

import { MarkerProps } from "@cofano/cck";

import GeofenceLabel from "view/locations/components/map/GeofenceLabel";
import { cleanlyDecodePolyline } from "view/locations/utils/LocationUtils";

import { SearchOnLocationsOnMapDocument } from "__generated__/view/locations/graphql/location.queries.generated";

export interface Point {
	lat: number;
	lng: number;
}

export interface OtherGeofence {
	id: number;
	description: string;
	encodedPolyline: string;
	line: Point[];
}

export type MapRef = google.maps.Map;
export type MapsRef = typeof google.maps;
export interface GeofenceMapApi {
	map: MapRef;
	maps: MapsRef;
}

export const OTHER_GEOFENCE_STYLE = {
	strokeColor: "#555",
	strokeOpacity: 0.9,
	strokeWeight: 2,
	fillColor: "#555",
	fillOpacity: 0.25,
};

const getCenter = (points: Point[]): Point => {
	const lats = points.map(point => point.lat);
	const lngs = points.map(point => point.lng);
	return {
		lat: (Math.min(...lats) + Math.max(...lats)) / 2,
		lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
	};
};

export const useOtherGeofences = (currentLocationId?: number | null): OtherGeofence[] => {

	const { data } = useQuery(SearchOnLocationsOnMapDocument, {
		variables: { locationSearch: { isActive: true }, pagination: null },
	});

	return useMemo(() => {
		const geofences: OtherGeofence[] = [];

		for (const location of data?.searchLocations.content || []) {
			if (!location.encodedPolyline || location.id === currentLocationId) {
				continue;
			}
			const line = cleanlyDecodePolyline(location.encodedPolyline)[0].line;
			if (line.length > 1) {
				geofences.push({
					id: location.id,
					description: location.description || "",
					encodedPolyline: location.encodedPolyline,
					line: line,
				});
			}
		}

		return geofences;
	}, [ data, currentLocationId ]);
};

export const getGeofenceLabels = (geofences: OtherGeofence[]): MarkerProps[] =>
	geofences.map(geofence => ({ ...getCenter(geofence.line), label: geofence.description, component: GeofenceLabel }));

export const useGeofenceShapes = (
	api: GeofenceMapApi | undefined,
	geofences: OtherGeofence[],
	clickable: boolean,
	onSelect: (geofence: OtherGeofence) => void,
) => {

	const onSelectRef = useRef(onSelect);
	onSelectRef.current = onSelect;

	useEffect(() => {
		if (!api) {
			return;
		}

		const shapes = geofences.map(geofence => {
			const polygon = new api.maps.Polygon({
				...OTHER_GEOFENCE_STYLE,
				paths: [ geofence.line ],
				clickable: clickable,
				zIndex: 1,
				map: api.map,
			});

			if (clickable) {
				// clicking a geofence selects it
				polygon.addListener("click", () => onSelectRef.current(geofence));

				// hover effect
				polygon.addListener("mouseover", () => polygon.setOptions({ fillOpacity: 0.45 }));
				polygon.addListener("mouseout", () => polygon.setOptions({ fillOpacity: OTHER_GEOFENCE_STYLE.fillOpacity }));
			}

			return polygon;
		});

		return () => shapes.forEach(shape => {
			api.maps.event.clearInstanceListeners(shape);
			shape.setMap(null);
		});
	}, [ api, geofences, clickable ]);
};
